import * as DOMUtils from "history/DOMUtils";
import type { CarConfigDispatchType, CarConfigStateType } from "../redux/store";
import type { AbortableFetchFnReturnType, FetchFnReturnType } from "../../../../common-deprecated/utils/backendFetch";
import { type CarConfigSettingsType, initCarConfigSettings } from "../redux/slices/CarConfigSettingsSlice";
import { initCarConfigItems } from "../redux/slices/CarConfigItemSlice";
import type { FetchCarDataType } from "../../../shared-logic/server/data/FetchCar";
import { initCarConfigCar } from "../redux/slices/CarConfigCarSlice";
import { initCarConfigMotorization } from "../redux/slices/CarConfigMotorizationSlice";
import { type GradeType, initCarConfigGrades } from "../redux/slices/CarConfigGradesSlice";
import {
    hasAlternativeEcoSpecFlow,
    searchModelMapValue,
} from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type { FetchSpecsEquipmentDataType } from "../../../shared-logic/server/data/FetchSpecifications";
import {
    setTradeInAemExtData,
    setTradeInAemExtDataFailed,
} from "../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import type { TradeInAemExternalDataType } from "../../../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import { fetchIntegratedTradein } from "../utils/carConfigSettingsUtils";
import type { SavedConfigType } from "../../../shared-logic/types/SaveTypes";
import {
    type CarConfigAtmosphereFinanceType,
    initCarConfigAtmospheres,
    initCarConfigFinanceAtmospheres,
    setCarConfigFinanceAtmospheresState,
} from "../redux/slices/CarConfigAtmosphereSlice";
import type { AtmosphereType } from "../utils/types/AtmosphereType";
import type { CompareSpecsType } from "../utils/types/CompareSpecsType";
import { initCarConfigCompareSpecs } from "../redux/slices/CarConfigCompareSpecsSlice";
import type { FetchItemsType } from "../server/fetchItems";
import { initAvailableInOtherGrades } from "../redux/slices/CarConfigAvailableInOtherGradesSlice";
import type { FetchAvailableInOtherGradesType } from "../server/fetchCarConfigAvailableInOtherGrades";
import { getTechSpecsAndEquipmentBody } from "../../../shared-logic/utils/techSpecUtils";
import type {
    FetchConfigurationSpecificationsBodyType,
    FetchTechSpecAndEquipmentBodyType,
} from "../../../../common-deprecated/types/SpecTypes";
import { setSpecs } from "../redux/slices/CarConfigSpecSlice";
import { maximizeBasket } from "../../../shared-logic/utils/basketUtils";
import {
    type FetchEcoConsumptionAceConfigType,
    type FetchEcoConsumptionCostsType,
    FETCH_ECO_CONSUMPTION_CONFIGURATION,
    FETCH_ECO_CONSUMPTION_COSTS,
    fetchEcoConsumptionAceConfigUtil,
    fetchEcoConsumptionCostsUtil,
} from "../../../shared-modules/eco-consumption-section/utils/ecoConsumptionConfigurationUtils";
import {
    initEcoConsumptionConfiguration,
    resetEcoConsumptionCosts,
    setEcoConsumptionCosts,
} from "../../../shared-modules/eco-consumption-section/redux/reducers/EcoConsumptionReducer";
import { AemFmComponent } from "../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { showAemMonthlyRates } from "../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { FINANCE_TIMEOUT } from "../../../../common-deprecated/constants";
import { getConfigSpecsPayloadSelector } from "../utils/externalPriceUtils";
import { getRequestFailureStatus, isRequestPossible } from "../../../shared-logic/utils/abortedRequestUtils";
import { isRetailer } from "../../../../common-deprecated/utils";
import { setDefaultCarId } from "../redux/slices/CarConfigNavigationSlice";
import type { MotorizationType } from "../../../shared-logic/types/Motorization";
import { showPrice } from "../../../../common-deprecated/settings/utils/financeSettingUtilsCommon";
import type { CarType } from "../../../shared-logic/types/CarType";
import {
    finishExternalPriceValueFetch,
    startExternalPriceValueFetch,
} from "../redux/slices/carConfigExternalPriceSlice";
import type { ExternalPriceCalulatedPriceType } from "../../../shared-logic/types/externalPriceTypes";
import type { CO2SpecsType } from "../../../shared-logic/types/ConfigurationSpecificationsObjectType";
import { RequestStatusEnum } from "../../../../common-deprecated/types/CommonTypes";

// ----------------------------------------------------------------------
// Fetch types
// ----------------------------------------------------------------------
export const FETCH_CAR_CONFIG_SETTINGS = "fetchCarConfigettings";
export const FETCH_CAR_CONFIG_ITEMS = "fetchCarConfigItems";
export const FETCH_CAR_CONFIG_DEFAULT_CAR = "fetchCarConfigModel";
export const FETCH_CAR_CONFIG_CAR = "fetchCarConfigCar";
export const FETCH_CAR_CONFIG_MOTORIZATION = "fetchCarConfigMotorization";
export const FETCH_CAR_CONFIG_GRADES = "fetchCarConfigGrades";
export const FETCH_CAR_CONFIG_SPECS = "fetchCarConfigSpecs";
export const FETCH_TRADE_IN_VALUATION = "fetchTradeInValuation";
export const FETCH_CAR_CONFIG_COMPARE_SPECS = "fetchCarConfigCompareSpecs";
export const FETCH_CAR_CONFIG_AVAILABLE_IN_OTHER_GRADES = "fetchCarConfigAvailbleInOtherGrades";
export const FETCH_CAR_CONFIG_EXTERNAL_PRICE = "fetchCarConfigExternalPrice";

// Fetch types specific for lexus LBX view
export const FETCH_CAR_CONFIG_ATMOSPHERES = "fetchCarConfigAtmospheres";
export const FETCH_CAR_CONFIG_ATMOSPHERE_PRICES = "fetchCarConfigAtmospherePrices";

export type FetchCarConfigSettingsType = {
    fetchType: typeof FETCH_CAR_CONFIG_SETTINGS;
    modelId: string;
    result?: FetchFnReturnType<CarConfigSettingsType | null>;
};

export type FetchCarConfigItemsType = {
    fetchType: typeof FETCH_CAR_CONFIG_ITEMS;
    modelId: string;
    carId: string;
    result?: FetchFnReturnType<FetchItemsType>;
};

export type FetchCarConfigDefaultCarType = {
    fetchType: typeof FETCH_CAR_CONFIG_DEFAULT_CAR;
    modelId: string;
    result?: string;
};

export type FetchCarConfigCarType = {
    fetchType: typeof FETCH_CAR_CONFIG_CAR;
    result?: FetchFnReturnType<FetchCarDataType>;
    modelId: string;
    carId: string;
    tyCodeConfig?: SavedConfigType | null;
};

export type FetchCarConfigGradesType = {
    fetchType: typeof FETCH_CAR_CONFIG_GRADES;
    result?: FetchFnReturnType<GradeType[]>;
    modelId: string;
};

export type FetchCarConfigMotorizationType = {
    fetchType: typeof FETCH_CAR_CONFIG_MOTORIZATION;
    result?: FetchFnReturnType<MotorizationType[]>;
    modelId: string;
    bodyTypeId: string;
    gradeId: string;
    carId: string;
};

export type FetchCarConfigSpecsType = {
    fetchType: typeof FETCH_CAR_CONFIG_SPECS;
    body: FetchTechSpecAndEquipmentBodyType;
    result?: FetchFnReturnType<FetchSpecsEquipmentDataType>;
};

export type FetchCarConfigAtmospheresType = {
    fetchType: typeof FETCH_CAR_CONFIG_ATMOSPHERES;
    modelId: string;
    modelCode: string;
    result?: FetchFnReturnType<AtmosphereType[]>;
};

export type FetchCarConfigAtmospherePricesType = {
    fetchType: typeof FETCH_CAR_CONFIG_ATMOSPHERE_PRICES;
    atmospheres: string[];
    timeout?: number;
    result?: AbortableFetchFnReturnType<CarConfigAtmosphereFinanceType["cars"] | null>;
};

type FetchTradeInValuationType = {
    fetchType: typeof FETCH_TRADE_IN_VALUATION;
    quoteId: string;
    result?: FetchFnReturnType<TradeInAemExternalDataType | null>;
};

export type FetchCarConfigCompareSpecsType = {
    fetchType: typeof FETCH_CAR_CONFIG_COMPARE_SPECS;
    modelId: string;
    result?: FetchFnReturnType<CompareSpecsType | null>;
};

export type FetchCarConfigAvailableInOtherGradesType = {
    fetchType: typeof FETCH_CAR_CONFIG_AVAILABLE_IN_OTHER_GRADES;
    modelId: string;
    gradeId: string;
    bodyTypeId: string;
    carId: string;
    result?: FetchFnReturnType<FetchAvailableInOtherGradesType | null>;
};

export type FetchCarConfigExternalPriceType = {
    fetchType: typeof FETCH_CAR_CONFIG_EXTERNAL_PRICE;
    car: CarType;
    configSpecsPayload: FetchConfigurationSpecificationsBodyType;
    result?: FetchFnReturnType<{ calculatedPrice: ExternalPriceCalulatedPriceType; co2Specs: CO2SpecsType | null }>;
};

export const fetchCarConfigSettings = (modelId: string): FetchCarConfigSettingsType => ({
    fetchType: FETCH_CAR_CONFIG_SETTINGS,
    modelId,
});

export const fetchCarConfigItems = (modelId: string, carId: string): FetchCarConfigItemsType => ({
    fetchType: FETCH_CAR_CONFIG_ITEMS,
    modelId,
    carId,
});

export const fetchCarConfigDefaultCar = (modelId: string): FetchCarConfigDefaultCarType => ({
    fetchType: FETCH_CAR_CONFIG_DEFAULT_CAR,
    modelId,
});

export const fetchCarConfigCar = (
    modelId: string,
    carId: string,
    tyCodeConfig: SavedConfigType | null,
): FetchCarConfigCarType => ({
    fetchType: FETCH_CAR_CONFIG_CAR,
    modelId,
    carId,
    tyCodeConfig,
});

export const fetchCarConfigMotorization = (
    modelId: string,
    bodyTypeId: string,
    gradeId: string,
    carId: string,
): FetchCarConfigMotorizationType => ({
    fetchType: FETCH_CAR_CONFIG_MOTORIZATION,
    modelId,
    bodyTypeId,
    gradeId,
    carId,
});

export const fetchCarConfigGrades = (modelId: string): FetchCarConfigGradesType => ({
    fetchType: FETCH_CAR_CONFIG_GRADES,
    modelId,
});

export const fetchCarConfigSpecs = (body: FetchTechSpecAndEquipmentBodyType): FetchCarConfigSpecsType => ({
    fetchType: FETCH_CAR_CONFIG_SPECS,
    body,
});

export const fetchCarConfigAtmospheres = (modelId: string, modelCode: string): FetchCarConfigAtmospheresType => ({
    fetchType: FETCH_CAR_CONFIG_ATMOSPHERES,
    modelId,
    modelCode,
});

export const fetchCarConfigAtmospherePrices = (
    atmospheres: string[],
    timeout?: number,
): FetchCarConfigAtmospherePricesType => ({
    fetchType: FETCH_CAR_CONFIG_ATMOSPHERE_PRICES,
    atmospheres,
    timeout,
});

export const fetchTradeInValuation = (quoteId: string): FetchTradeInValuationType => ({
    fetchType: FETCH_TRADE_IN_VALUATION,
    quoteId,
});

export const fetchCarConfigCompareSpecs = (modelId: string): FetchCarConfigCompareSpecsType => ({
    fetchType: FETCH_CAR_CONFIG_COMPARE_SPECS,
    modelId,
});

export const fetchCarConfigAvailableInOtherGrades = (
    modelId: string,
    gradeId: string,
    bodyTypeId: string,
    carId: string,
): FetchCarConfigAvailableInOtherGradesType => ({
    fetchType: FETCH_CAR_CONFIG_AVAILABLE_IN_OTHER_GRADES,
    modelId,
    gradeId,
    bodyTypeId,
    carId,
});

export const fetchCarConfigExternalPrice = (
    car: CarType,
    configSpecsPayload: FetchConfigurationSpecificationsBodyType,
): FetchCarConfigExternalPriceType => ({
    fetchType: FETCH_CAR_CONFIG_EXTERNAL_PRICE,
    car,
    configSpecsPayload,
});

export type CarConfigFetchTypes =
    | FetchCarConfigSettingsType
    | FetchCarConfigDefaultCarType
    | FetchCarConfigCarType
    | FetchCarConfigItemsType
    | FetchCarConfigGradesType
    | FetchCarConfigMotorizationType
    | FetchCarConfigSpecsType
    | FetchCarConfigAtmospheresType
    | FetchCarConfigAtmospherePricesType
    | FetchTradeInValuationType
    | FetchCarConfigCompareSpecsType
    | FetchCarConfigAvailableInOtherGradesType
    | FetchEcoConsumptionAceConfigType
    | FetchEcoConsumptionCostsType
    | FetchCarConfigExternalPriceType;

/**
 * Getter for carconfig fetches
 * All Fetches that are car specific will/should not be fetched server side. This is to have a quick SSR skeleton that is shown for our components
 * Model specific calls can be easily cached using redis so these can be done server side.
 */
export const getCarConfigComponentFetches = (state: CarConfigStateType): CarConfigFetchTypes[] => {
    const isSSR = !DOMUtils.canUseDOM;

    const fetches: CarConfigFetchTypes[] = [];
    const {
        carConfigNavigation,
        carConfigSettings,
        items,
        grades,
        commonSettings,
        carConfigMotorization,
        car,
        specs,
        tradeInAemExternalData,
        carConfigAtmosphere,
        save,
        availableInOtherGrades,
        ecoConsumption,
        externalPrice,
    } = state;

    if (carConfigNavigation.step) {
        const { modelId, carId } = carConfigNavigation;
        const modelMapModel = searchModelMapValue<CommonSettingsType>(commonSettings, modelId);
        const modelMapModelId = modelMapModel?.id;

        // Settings always need to be the first thing to be fetched
        if (!carConfigSettings.initialized && modelMapModelId) {
            fetches.push(fetchCarConfigSettings(modelMapModelId));
        }

        // If there is no carId defined in the URL fallback to the carconfigNavigation carId which take the value from carDB/Texus, if that fails, use the first grade's defaultCarID.
        const chosenCarId = carId || carConfigNavigation.defaultCarId || grades?.items[0]?.defaultCarId;

        // This is added for the engine/grade selection logic. The selection of the engine changes the query so this statement should refetch the car when this happens
        const carIsSelected = !!carId && !!car.id && carId !== car.id;

        // The custom SSR loading is disabled for retailer
        // !!: This is disabled for now as there were some issues with certain data not loading. This will be checked with OR-10903
        const disableClientSideLoading =
            (isRetailer(commonSettings) && commonSettings.brand === "toyota") ||
            commonSettings.query.useLbxJourney ||
            true;

        // uncached data will not be loaded for SSR to get a quick skeleton component for the carconfig.
        // This server side render will be cached on the dispatcher cache
        if (modelMapModelId && chosenCarId && (!isSSR || disableClientSideLoading)) {
            if (!car.id || carIsSelected) {
                // If the fetch has been triggered by the engine selection the tyCode config should not be sent
                // as this will overwrite the carID given through via the engine selection with the current carID
                fetches.push(fetchCarConfigCar(modelMapModelId, chosenCarId, carIsSelected ? null : save.data));
            }

            if (!items.initialized || carIsSelected) {
                fetches.push(fetchCarConfigItems(modelMapModelId, chosenCarId));
            }
        }

        // Get the default carID via texus. This checks if there is a car promoted in carDB, if not, take the cheapest.
        if (modelMapModelId && !carConfigNavigation.defaultCarId && !chosenCarId) {
            fetches.push(fetchCarConfigDefaultCar(modelMapModelId!));
        }

        if (modelMapModelId && !grades.initialized) {
            fetches.push(fetchCarConfigGrades(modelMapModelId));
        }

        if (modelMapModelId && car.grade.id && car.body.id && (!carConfigMotorization.initialized || carIsSelected)) {
            fetches.push(fetchCarConfigMotorization(modelMapModelId, car.body.id, car.grade.id, chosenCarId));
        }

        if (
            carConfigSettings.initialized &&
            !carConfigSettings.disableAvailableInOtherGrades &&
            !availableInOtherGrades.initialized &&
            modelMapModelId &&
            car.grade.id &&
            car.body.id
        ) {
            fetches.push(fetchCarConfigAvailableInOtherGrades(modelMapModelId, car.grade.id, car.body.id, car.id));
        }

        if (carConfigSettings.initialized && modelMapModelId && car.initialized && items.initialized) {
            if (
                carConfigSettings.enableExternalPrice &&
                showPrice(commonSettings) &&
                externalPrice.state === RequestStatusEnum.Fetching
            ) {
                // During the first fetch the basket is only initialized in postFetch but this fetch already requires it.
                // If the basket is not available yet use the maximizeBasket function to retrieve a basket object.
                const basket = state.basket.initialized
                    ? state.basket
                    : maximizeBasket(state.car, state.items, state.basket, commonSettings.query);

                const configSpecsPayload = getConfigSpecsPayloadSelector({ ...state, basket }, true);

                fetches.push(fetchCarConfigExternalPrice(car, configSpecsPayload));
            }
        }

        if ((modelMapModelId && items.initialized && !specs.initialized) || carIsSelected) {
            // During the first fetch the basket is only initialized in postFetch but this fetch already requires it.
            // If the basket is not available yet use the maximizeBasket function to retrieve a basket object.
            const basket = state.basket.initialized
                ? state.basket
                : maximizeBasket(state.car, state.items, state.basket, carConfigNavigation.basketParams);

            const body = getTechSpecsAndEquipmentBody(
                { ...state.car, id: chosenCarId },
                basket,
                state.items,
                state.utils.financeOption,
            );
            fetches.push(fetchCarConfigSpecs(body));
        }

        if (commonSettings.query.useLbxJourney) {
            if (modelId && !carConfigAtmosphere.initialized) {
                fetches.push(fetchCarConfigAtmospheres(modelId, car.model.internalCode));
                fetches.push(fetchCarConfigCompareSpecs(modelId));
            }
            if (
                modelId &&
                carConfigSettings.initialized &&
                carConfigAtmosphere.initialized &&
                !carConfigSettings.disableFinanceService &&
                isRequestPossible(carConfigAtmosphere.finance.state) &&
                showAemMonthlyRates(commonSettings, AemFmComponent.Carconfig, "new", car.model.internalCode)
            ) {
                const timeout = !DOMUtils.canUseDOM ? FINANCE_TIMEOUT : undefined;
                const allCarIds = carConfigAtmosphere.atmospheres.map(({ car: atomsCar }) => atomsCar.id);
                fetches.push(fetchCarConfigAtmospherePrices(allCarIds, timeout));
            }
        }

        if (modelMapModel && hasAlternativeEcoSpecFlow(commonSettings) && (car.id || carIsSelected)) {
            if (!ecoConsumption.initialized)
                fetches.push(fetchEcoConsumptionAceConfigUtil(modelMapModel?.internalCode));
            if (carConfigSettings.initialized && isRequestPossible(ecoConsumption.costsStatus, carIsSelected)) {
                // During the first fetch the basket is only initialized in postFetch but this fetch already requires it.
                // If the basket is not available yet use the maximizeBasket function to retrieve a basket object.
                const basket = state.basket.initialized
                    ? state.basket
                    : maximizeBasket(state.car, state.items, state.basket, carConfigNavigation.basketParams);

                const config = getConfigSpecsPayloadSelector({ ...state, basket }, true);
                if (config) {
                    const timeout = !DOMUtils.canUseDOM ? FINANCE_TIMEOUT : undefined;
                    fetches.push(
                        fetchEcoConsumptionCostsUtil(carConfigSettings.ecoConsumptionCostService, config, timeout),
                    );
                }
            }
        }
    }

    // Integrated Trade-in
    // We can assume that the quoteId which is extracted from the tyCode is always valid. An invalid tyCode with incompatible trade-in / finance should never be generated frontend wise.
    if (fetchIntegratedTradein(carConfigSettings, commonSettings)) {
        const quoteId = commonSettings.query.quoteId || state.save?.data?.Personal?.Parameters?.TradeInQuoteID;
        if (quoteId && !tradeInAemExternalData.failed && !tradeInAemExternalData.data.vehicleValue) {
            fetches.push(fetchTradeInValuation(quoteId));
        }
    }

    return fetches;
};

// ----------------------------------------------------------------------
// Dispatcher
// ----------------------------------------------------------------------
export const carConfigFetchDispatcher = (
    data: Required<CarConfigFetchTypes>,
    dispatch: CarConfigDispatchType,
): void => {
    switch (data.fetchType) {
        case FETCH_CAR_CONFIG_SETTINGS:
            if (data.result.fetchResult) {
                dispatch(initCarConfigSettings(data.result.fetchResult));
            }
            break;

        case FETCH_CAR_CONFIG_DEFAULT_CAR:
            if (data.result) {
                dispatch(setDefaultCarId(data.result));
            }
            break;

        case FETCH_CAR_CONFIG_CAR:
            if (data.result.fetchResult) {
                const { car } = data.result.fetchResult;
                dispatch(initCarConfigCar(car));

                // The carconfig as an SPA can be reloaded with a different motorization which re-triggers the car
                // if externalPrice is enabled it should retrigger the external price to recalculate. Doing this here will cause it do do it in the same datafetchLoop
                dispatch(startExternalPriceValueFetch({}));
            }
            break;

        case FETCH_CAR_CONFIG_ITEMS:
            if (data.result.fetchResult) {
                const { items } = data.result.fetchResult;
                dispatch(initCarConfigItems(items));
            }
            break;

        case FETCH_CAR_CONFIG_EXTERNAL_PRICE:
            if (data.result.fetchResult) {
                dispatch(finishExternalPriceValueFetch(data.result.fetchResult));
            }
            break;

        case FETCH_CAR_CONFIG_AVAILABLE_IN_OTHER_GRADES:
            if (data.result.fetchResult) {
                const { availableInOtherGradesData } = data.result.fetchResult;
                dispatch(initAvailableInOtherGrades(availableInOtherGradesData));
            }
            break;

        case FETCH_CAR_CONFIG_MOTORIZATION:
            if (data.result.fetchResult) {
                dispatch(initCarConfigMotorization({ motorization: data.result.fetchResult }));
            }
            break;

        case FETCH_CAR_CONFIG_GRADES:
            if (data.result.fetchResult) {
                dispatch(initCarConfigGrades({ items: data.result.fetchResult, initialized: true }));
            }
            break;

        case FETCH_CAR_CONFIG_SPECS:
            if (data.result.fetchResult) {
                dispatch(setSpecs(data.result.fetchResult));
            }
            break;

        case FETCH_CAR_CONFIG_ATMOSPHERES:
            if (data.result.fetchResult) {
                dispatch(initCarConfigAtmospheres({ atmospheres: data.result.fetchResult }));
            }
            break;

        case FETCH_CAR_CONFIG_ATMOSPHERE_PRICES:
            if (data.result.fetchResult) {
                dispatch(initCarConfigFinanceAtmospheres(data.result.fetchResult));
            } else {
                // Only when finance is null, did the fetch fail.
                // Otherwise the finance timed out thus should be retriggered client-side
                dispatch(setCarConfigFinanceAtmospheresState(getRequestFailureStatus(data.result)));
            }
            break;

        case FETCH_CAR_CONFIG_COMPARE_SPECS:
            if (data.result?.fetchResult) {
                dispatch(initCarConfigCompareSpecs({ grades: data.result.fetchResult }));
            }
            break;

        case FETCH_TRADE_IN_VALUATION: {
            if (data.result.fetchResult) dispatch(setTradeInAemExtData(data.result.fetchResult));
            else dispatch(setTradeInAemExtDataFailed(true));
            break;
        }

        case FETCH_ECO_CONSUMPTION_CONFIGURATION: {
            const { result } = data;
            const { labels, ace } = result?.fetchResult || {
                labels: null,
                ace: null,
            };

            dispatch(
                initEcoConsumptionConfiguration({
                    labels,
                    ace,
                }),
            );
            break;
        }

        case FETCH_ECO_CONSUMPTION_COSTS: {
            const { result } = data;
            if (result.fetchResult) {
                dispatch(setEcoConsumptionCosts({ costs: result.fetchResult }));
            } else {
                dispatch(resetEcoConsumptionCosts(getRequestFailureStatus(result)));
            }
            break;
        }
    }
};
